import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Lock, Package, DollarSign, Users, Truck, CheckCircle2, LogOut, RefreshCw, Star, Check, Trash2, MessageSquareQuote,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm border border-brand-goldlight/40">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-sand flex items-center justify-center">
        <Icon className="h-5 w-5 text-brand-brass" />
      </div>
      <div>
        <div className="font-serif text-2xl font-bold text-brand-ink">{value}</div>
        <div className="text-xs text-brand-brown/70">{label}</div>
      </div>
    </div>
  </div>
);

const Admin = () => {
  const { toast } = useToast();
  const [token, setToken] = useState(() => localStorage.getItem('lr_admin_token') || '');
  const [password, setPassword] = useState('');
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const authed = !!token;

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const headers = { 'X-Admin-Token': tk };
      const [o, s, r] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/reviews`, { headers }),
      ]);
      setOrders(o.data);
      setStats(s.data);
      setReviews(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('lr_admin_token');
        setToken('');
        toast({ title: 'Session expired', description: 'Please sign in again.', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem('lr_admin_token', data.token);
      setToken(data.token);
      setPassword('');
    } catch (err) {
      toast({ title: 'Incorrect password', variant: 'destructive' });
    }
  };

  const logout = () => {
    localStorage.removeItem('lr_admin_token');
    setToken('');
    setOrders([]);
    setReviews([]);
    setStats(null);
  };

  const markShipped = async (orderId) => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/ship`, {}, { headers: { 'X-Admin-Token': token } });
      toast({ title: 'Marked as shipped', description: orderId });
      load(token);
    } catch (err) {
      toast({ title: 'Could not update order.', variant: 'destructive' });
    }
  };

  const moderateReview = async (id, action) => {
    try {
      await axios.post(`${API}/admin/reviews/${id}/${action}`, {}, { headers: { 'X-Admin-Token': token } });
      toast({ title: action === 'approve' ? 'Review approved' : 'Review removed' });
      load(token);
    } catch (err) {
      toast({ title: 'Could not update review.', variant: 'destructive' });
    }
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 bg-sand/40">
        <form onSubmit={login} className="bg-white rounded-2xl p-8 shadow-lg w-full max-w-sm border border-brand-goldlight/50">
          <div className="h-14 w-14 rounded-full bg-sand flex items-center justify-center mx-auto mb-5">
            <Lock className="h-6 w-6 text-brand-brass" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-brand-ink text-center">Admin Access</h1>
          <p className="text-sm text-brand-brown/70 text-center mt-1 mb-6">Limpid Reader order dashboard</p>
          <Label className="text-brand-ink">Password</Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 bg-cream border-brand-goldlight"
            placeholder="Enter admin password"
          />
          <Button type="submit" className="w-full mt-5 bg-brand-ink hover:bg-brand-brown text-cream rounded-full h-11">
            Sign In
          </Button>
        </form>
      </div>
    );
  }

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 min-h-[70vh]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold text-brand-ink">Order Dashboard</h1>
          <p className="text-brand-brown/70 text-sm">Manage orders and shipments</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => load(token)} variant="outline" className="border-brand-gold text-brand-ink hover:bg-brand-gold hover:text-white rounded-full gap-2">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={logout} variant="outline" className="border-brand-goldlight text-brand-brown rounded-full gap-2">
            <LogOut className="h-4 w-4" /> Logout
          </Button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <StatCard icon={Package} label="Paid Orders" value={stats.paid_orders} />
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue}`} />
          <StatCard icon={Truck} label="Pending Shipments" value={stats.pending_shipments} />
          <StatCard icon={MessageSquareQuote} label="Pending Reviews" value={stats.pending_reviews ?? 0} />
          <StatCard icon={Users} label="Subscribers" value={stats.subscribers} />
        </div>
      )}

      {/* Reviews moderation */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-goldlight/40 overflow-hidden mb-10">
        <div className="px-6 py-4 border-b border-brand-goldlight/40 flex items-center gap-2">
          <MessageSquareQuote className="h-5 w-5 text-brand-brass" />
          <h2 className="font-serif text-lg font-bold text-brand-ink">Reader Reviews ({reviews.length})</h2>
        </div>
        {reviews.length === 0 ? (
          <div className="p-12 text-center text-brand-brown/60">No reviews yet.</div>
        ) : (
          <div className="divide-y divide-brand-goldlight/30">
            {reviews.map((r) => (
              <div key={r.id} className="p-5 flex flex-col lg:flex-row lg:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif font-bold text-brand-ink">{r.name}</span>
                    <span className="flex items-center">
                      {Array.from({ length: Math.max(0, Math.min(5, r.rating)) }).map((_, i) => (
                        <Star key={i} className="h-3.5 w-3.5 fill-brand-gold text-brand-gold" />
                      ))}
                    </span>
                    {r.approved ? (
                      <Badge className="bg-green-100 text-green-800 border-0">Published</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-0">Pending</Badge>
                    )}
                    <span className="text-xs text-brand-brass italic">on {r.book}</span>
                  </div>
                  {r.message && <p className="text-sm text-brand-brown/85 mt-1.5 leading-relaxed">“{r.message}”</p>}
                  <p className="text-xs text-brand-brown/50 mt-1">{r.email}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!r.approved && (
                    <Button
                      onClick={() => moderateReview(r.id, 'approve')}
                      className="bg-green-700 hover:bg-green-800 text-white rounded-full h-9 px-4 text-sm gap-1.5"
                    >
                      <Check className="h-4 w-4" /> Approve
                    </Button>
                  )}
                  <Button
                    onClick={() => moderateReview(r.id, 'reject')}
                    variant="outline"
                    className="border-brand-goldlight text-brand-brown hover:bg-red-50 hover:text-red-700 hover:border-red-300 rounded-full h-9 px-4 text-sm gap-1.5"
                  >
                    <Trash2 className="h-4 w-4" /> {r.approved ? 'Remove' : 'Reject'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-brand-goldlight/40 overflow-hidden">
        <div className="px-6 py-4 border-b border-brand-goldlight/40">
          <h2 className="font-serif text-lg font-bold text-brand-ink">All Orders ({orders.length})</h2>
        </div>
        {orders.length === 0 ? (
          <div className="p-12 text-center text-brand-brown/60">No orders yet.</div>
        ) : (
          <div className="divide-y divide-brand-goldlight/30">
            {orders.map((o) => (
              <div key={o.order_id} className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-brand-ink">{o.order_id}</span>
                    <Badge className={`${o.status === 'paid' ? 'bg-green-100 text-green-800' : o.status === 'created' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'} border-0`}>
                      {o.status}
                    </Badge>
                    {o.has_physical ? (
                      <Badge className="bg-brand-ink text-cream border-0">Physical</Badge>
                    ) : (
                      <Badge className="bg-sand text-brand-brown border-0">Digital</Badge>
                    )}
                    {o.promo_code && <Badge className="bg-brand-goldlight text-brand-brown border-0">{o.promo_code}</Badge>}
                    <span className="text-xs text-brand-brown/60">{fmtDate(o.created_at)}</span>
                  </div>
                  <div className="text-sm text-brand-brown/80 mt-1">
                    {o.customer?.name} &middot; {o.customer?.email}
                  </div>
                  <div className="text-xs text-brand-brown/60 mt-0.5">
                    {o.items?.map((i) => `${i.title} (${i.format} x${i.qty})`).join(', ')}
                  </div>
                  {o.has_physical && o.shipping?.address && (
                    <div className="text-xs text-brand-brass mt-0.5">
                      Ship to: {o.shipping.address}{o.shipping.city ? `, ${o.shipping.city}` : ''}{o.shipping.country ? `, ${o.shipping.country}` : ''}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="text-right">
                    <div className="font-serif text-lg font-bold text-brand-ink">${o.total}</div>
                    {o.discount > 0 && <div className="text-xs text-brand-brass">saved ${o.discount}</div>}
                  </div>
                  {o.has_physical && o.status === 'paid' && (
                    o.shipped ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-green-700 font-medium">
                        <CheckCircle2 className="h-4 w-4" /> Shipped
                      </span>
                    ) : (
                      <Button
                        onClick={() => markShipped(o.order_id)}
                        className="bg-brand-gold hover:bg-brand-brass text-brand-ink hover:text-white rounded-full h-9 px-4 text-sm gap-1.5"
                      >
                        <Truck className="h-4 w-4" /> Mark Shipped
                      </Button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
